import React, { createContext, type PropsWithChildren, useEffect, useMemo } from 'react';

interface PageLoaderRegistryCtx {
  addPageLoader: (id: string) => void;
  removePageLoader: (id: string) => void;
}

export const PageLoaderRegistryContext = createContext<PageLoaderRegistryCtx | null>(null);

type PageLoaderRegistryProps = PropsWithChildren<{
  subscribe?: (currentLoaderCount: number) => void;
}>;

const PageLoaderRegistry: React.FC<PageLoaderRegistryProps> = ({ children, subscribe }) => {
  const pageLoaderRegistry = React.useRef<string[]>([]);

  // call subscribe once initially to initialize subscribers
  useEffect(() => {
    subscribe?.(pageLoaderRegistry.current.length);
  }, [subscribe]);

  const ctxValue = useMemo<PageLoaderRegistryCtx>(
    () => ({
      addPageLoader: (id: string) => {
        if (!pageLoaderRegistry.current.includes(id)) {
          pageLoaderRegistry.current.push(id);
          subscribe?.(pageLoaderRegistry.current.length);
        }
      },
      removePageLoader: (id: string) => {
        const index = pageLoaderRegistry.current.indexOf(id);
        if (index >= 0) {
          pageLoaderRegistry.current.splice(index, 1);
          subscribe?.(pageLoaderRegistry.current.length);
        }
      },
    }),
    [subscribe],
  );

  return (
    <PageLoaderRegistryContext.Provider value={ctxValue}>
      {children}
    </PageLoaderRegistryContext.Provider>
  );
};

export default PageLoaderRegistry;
